"use client";

import { WorkplaceRequestElement } from "@gettactic/api";
import { StatusBadge } from "@gettactic/components/src/data-display/StatusBadge/StatusBadge";
import { Check } from "lucide-react";
import React from "react";

const statusColor = (
  status: string
): React.ComponentProps<typeof StatusBadge>["color"] =>
  status === "pending" ? "orange" : status === "closed" ? "green" : "neutral";

interface RequestConfirmationProps {
  request: WorkplaceRequestElement;
  /** Name of the person the request was filed for (the requester). */
  submittedByName?: string;
  /** Human-readable location, e.g. "Desk 39 · Building 3 · 4th Floor". */
  locationLabel?: string;
  /** Category the request's type belongs to, named as the reviewing team. */
  categoryName?: string;
}

const Row: React.FC<{
  label: string;
  isLast?: boolean;
  children: React.ReactNode;
}> = ({ label, isLast, children }) => (
  <div
    className={`flex items-center justify-between gap-[12px] px-[14px] py-[11px] ${
      isLast ? "" : "border-neutral-100 border-b"
    }`}
  >
    <span className="shrink-0 grow-0 basis-auto text-left font-inter text-[13px] text-neutral-600 leading-[18.2px] antialiased">
      {label}
    </span>
    <div className="min-w-0 overflow-hidden text-ellipsis text-right font-medium text-neutral-1000 text-[13px]">
      {children}
    </div>
  </div>
);

export const RequestConfirmation: React.FC<RequestConfirmationProps> = ({
  request,
  submittedByName,
  locationLabel,
  categoryName
}) => {
  const reviewingTeam = categoryName ? `The ${categoryName} team` : "The team";
  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-7 w-7 text-green-700" strokeWidth={2.5} />
        </div>
        <div>
          <h3 className="font-mona font-semibold text-[20px] text-neutral-900 tracking-[-0.01em]">
            Request submitted
          </h3>
          <p className="text-neutral-700 text-[13px]">
            {request.display_id} · {request.type?.name}
          </p>
        </div>
      </div>

      <div className="w-full max-w-md rounded-xl border border-neutral-100">
        <Row label="Summary">
          <span className="truncate">{request.title}</span>
        </Row>
        {locationLabel ? <Row label="Location">{locationLabel}</Row> : null}
        <Row label="Submitted by">{submittedByName ?? "—"}</Row>
        <Row label="Status" isLast>
          <StatusBadge
            size="sm"
            color={statusColor(request.status)}
            label={request.sub_status?.name ?? request.status}
          />
        </Row>
      </div>

      <p className="max-w-[46ch] text-center text-[12px] text-neutral-600 leading-[1.5]">
        {reviewingTeam} will review your request. You'll get a notification when
        there's an update.
      </p>
    </div>
  );
};
